import { SCHEMES } from '@/components/EchartCustomized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import ChartIcons from '@/icons/ChartIcons'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Typogaphy } from 'vnetwork-ui'
import { getStatsByPeriod, StatsByPeriodPayload } from '../../../services'
import { ChartSkeleton } from './skeletons'

interface LeadInputChartProps {
    filterPayload: Omit<StatsByPeriodPayload, 'period'>
}

const LeadInputChart: React.FC<LeadInputChartProps> = ({ filterPayload }) => {
    const { t } = useTranslation()
    const [period, setPeriod] = useState<'day' | 'week' | 'month'>('day')

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.MARKETING,
                'lead-input-chart',
                period,
                filterPayload,
            ],
            queryFn: () => getStatsByPeriod({ period, ...filterPayload }),
        },
        { loadingComponent: <ChartSkeleton /> }
    )

    const stats = data?.results || []

    // Transform data for chart - sort by date ascending
    const chartData = useMemo(() => {
        if (!stats || stats.length === 0) return []

        return stats
            .filter((item) => item && item.date) // Filter out invalid items
            .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
            .map((item) => {
                const newContacts = item.new_leads ?? 0
                const assigned = item.assigned ?? 0
                return {
                    name: dayjs(item.date).format('DD/MM'),
                    new_contacts: newContacts,
                    unassigned: Math.max(0, newContacts - assigned),
                    assigned: assigned,
                    warm: item.warm_classified ?? 0,
                    sql: item.sale_qualified_classified ?? 0,
                    cool: item.cool_classified ?? 0,
                    unqualified: item.unqualified_classified ?? 0,
                }
            })
    }, [stats])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="bg-white rounded-md border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <ChartIcons className="text-primary w-4 h-4" />
                    <Typogaphy className="font-medium text-primary capitalize">
                        {t('lead-input-chart')}
                    </Typogaphy>
                </div>
                <div className="flex gap-1">
                    {(['day', 'week', 'month'] as const).map((p) => (
                        <button
                            key={p}
                            onClick={() => setPeriod(p)}
                            className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                period === p
                                    ? 'bg-primary text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {t(`period-${p}`)}
                        </button>
                    ))}
                </div>
            </div>

            {chartData.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                    {t('no-data')}
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11 }}
                            tickLine={false}
                        />
                        <YAxis tick={{ fontSize: 11 }} tickLine={false} />
                        <Tooltip
                            contentStyle={{
                                fontSize: 12,
                                borderRadius: 8,
                                border: '1px solid #e5e7eb',
                            }}
                        />
                        <Legend
                            wrapperStyle={{ fontSize: 11 }}
                            iconType="plainline"
                            iconSize={14}
                        />
                        <Line
                            type="monotone"
                            dataKey="new_contacts"
                            name={t('new-contacts')}
                            stroke={SCHEMES.default[14]}
                            strokeWidth={1.5}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="unassigned"
                            name={t('unassigned')}
                            stroke={SCHEMES.mono.gray[2]}
                            strokeWidth={1.5}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="assigned"
                            name={t('assigned-unprocessed')}
                            stroke={SCHEMES.vcloud[4]}
                            strokeWidth={1.5}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="warm"
                            name={t('warm-lead')}
                            stroke={SCHEMES.accent}
                            strokeWidth={1.5}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="sql"
                            name={t('sale-qualified-lead')}
                            stroke={SCHEMES.success[0]}
                            strokeWidth={1.5}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="cool"
                            name={t('cool-lead')}
                            stroke={SCHEMES.primary}
                            strokeWidth={1.5}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="unqualified"
                            name={t('unqualified')}
                            stroke={SCHEMES.danger}
                            strokeWidth={1.5}
                            dot={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            )}
        </div>
    )
}

export default LeadInputChart
